/**
 * Aprobación HHEE (ASIS-5351)
 * Transport DTOs (LC_*) and internal UI models for the overtime approval screen.
 */

import { OVERTIME_FLOW_PROFILE_CODE } from '@/api/domains/attendance/types/attendanceConfiguration.types';
import type { EmployeeFilteredStatus } from '@/api/domains/employee/types';

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

/**
 * Row status as resolved by the backend. `Pendiente`/`Parcial`/`Aprobada` describen la aprobación
 * (lo que ve el aprobador); `Justificada` describe la marca del revisor (ASIS-5358) y solo aparece
 * en `reviewerStatus`, que a grano de fila nunca es `Parcial`.
 */
export const OVERTIME_APPROVAL_STATUSES = [
  'Pendiente',
  'Parcial',
  'Aprobada',
  'Justificada',
] as const;
export type OvertimeApprovalStatus = (typeof OVERTIME_APPROVAL_STATUSES)[number];

/** Counter the user can select to filter the grid. `justified` es exclusivo del revisor. */
export const OVERTIME_COUNTER_KEYS = [
  'calculated',
  'pending',
  'approved',
  'notApproved',
  'justified',
] as const;
export type OvertimeCounterKey = (typeof OVERTIME_COUNTER_KEYS)[number];

/**
 * Perfil efectivo del usuario dentro del flujo de aprobación de HHEE, resuelto server-side.
 * Reutiliza `OVERTIME_FLOW_PROFILE_CODE` de la configuración de asistencia — no se redeclara.
 */
export type OvertimeFlowProfileCode =
  (typeof OVERTIME_FLOW_PROFILE_CODE)[keyof typeof OVERTIME_FLOW_PROFILE_CODE];

/** Origin of the monthly cuts returned by the backend. */
export type OvertimeCutsSource = 'company' | 'holding' | 'none';

/** Hour value in both formats, so the decimal switch never needs a refetch. */
export interface LC_HourValueOut {
  hhmmss: string;
  decimalValue: number;
}

/** Shared "no data yet" hour value — single source of truth for List and Detail mappers/hooks. */
export const EMPTY_HOUR_VALUE: LC_HourValueOut = { hhmmss: '00:00:00', decimalValue: 0 };

// ---------------------------------------------------------------------------
// Monthly cuts
// ---------------------------------------------------------------------------

export interface LC_OvertimeApprovalMonthlyCutItemOut {
  monthlyCutId: number;
  companyId: number;
  year: number;
  month: number;
  monthName: string;
  description: string;
  startDate: string;
  endDate: string;
  isClosed: boolean;
  appliesToAllCompanies: boolean;
}

export interface LC_GetOvertimeApprovalMonthlyCutsListOut {
  monthlyCuts: LC_OvertimeApprovalMonthlyCutItemOut[];
  defaultMonthlyCutId: number | null;
  cutsSource: OvertimeCutsSource;
}

/** Internal UI model for a monthly cut available in the period filters. */
export interface OvertimeApprovalMonthlyCut {
  monthlyCutId: number;
  companyId: number;
  year: number;
  month: number;
  monthName: string;
  description: string;
  isClosed: boolean;
  appliesToAllCompanies: boolean;
}

/** Internal UI model for the resolved set of monthly cuts. */
export interface OvertimeApprovalCuts {
  monthlyCuts: OvertimeApprovalMonthlyCut[];
  defaultMonthlyCutId: number | null;
  cutsSource: OvertimeCutsSource;
}

// ---------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------

export interface LC_ListOvertimeApprovalIn {
  monthlyCutId: number;
  filterCompanyId?: number | null;
  counterFilter?: OvertimeCounterKey | null;
  page?: number;
  pageSize?: number;
  searchText?: string;
  orderBy?: string;
  orderDirection?: string;
  /** Explicitly selected collaborators. Takes precedence over the dimensional filters. */
  employeeIds?: string;
  companyIds?: string;
  areaIds?: string;
  positionIds?: string;
  costCenterIds?: string;
  locationIds?: string;
  contractModalityIds?: string;
  groupIds?: string;
  employeeStatus?: EmployeeFilteredStatus;
}

export interface LC_OvertimeApprovalItemOut {
  employeeId: number;
  rut: string;
  employeeName: string;
  calculatedHours: LC_HourValueOut;
  approvedHours: LC_HourValueOut;
  /** Horas con marca de justificación del REVISOR para este colaborador (ASIS-5358). No son las
   *  aprobadas: es la etapa previa del flujo. La grilla del revisor muestra esta columna en lugar
   *  de "Aprobadas". Viaja en cero cuando el flujo no está habilitado. */
  reviewerJustifiedHours: LC_HourValueOut;
  status: OvertimeApprovalStatus;
}

export interface LC_OvertimeApprovalCountersOut {
  calculatedHours: LC_HourValueOut;
  pendingHours: LC_HourValueOut;
  approvedHours: LC_HourValueOut;
  notApprovedHours: LC_HourValueOut;
  /** Horas JUSTIFICADAS POR EL REVISOR (marca en `caHHEEFlujoAprobacionAuditoria`), no las horas
   *  aprobadas — que en el dominio legado también se llaman "justificadas" (ASIS-5358). La
   *  entregan tanto el listado como el detalle. */
  reviewerJustifiedHours: LC_HourValueOut;
}

export interface LC_PaginationMetadataOut {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface LC_ListOvertimeApprovalListOut {
  items: LC_OvertimeApprovalItemOut[];
  pagination: LC_PaginationMetadataOut;
  counters: LC_OvertimeApprovalCountersOut;
  isMonthlyCutClosed: boolean;
  /** Perfil efectivo del usuario en el flujo REVISOR -> APROBADOR, resuelto server-side desde el
   *  claim (ASIS-5358). Decide qué contadores, qué cuarta columna y qué acciones pinta la pantalla;
   *  la autorización real de cada acción la aplica el backend con 403. */
  viewerFlowProfile?: OvertimeFlowProfileCode | null;
}

/** Internal UI model for a grid row. */
export interface OvertimeApprovalRow {
  id: number;
  rut: string;
  employeeName: string;
  calculatedHours: LC_HourValueOut;
  approvedHours: LC_HourValueOut;
  /** Horas con marca del revisor (ASIS-5358). Alimenta la columna "Justificadas" del revisor. */
  reviewerJustifiedHours: LC_HourValueOut;
  status: OvertimeApprovalStatus;
}

// ---------------------------------------------------------------------------
// Bulk approval (ASIS-5353)
// ---------------------------------------------------------------------------

export interface LC_BulkApproveOvertimeIn {
  monthlyCutId: number;
  employeeIds: string;
}

/** Result per collaborator for a bulk approve/reverse operation. */
export type OvertimeBulkOutcome =
  | 'approved'
  | 'reversed'
  /** Row justified by the reviewer (ASIS-5358): a REVISOR mark was written for its
   *  (employee, journey, schedule, overtime type) key. */
  | 'justified'
  | 'alreadyResolved'
  | 'notProcessed'
  /** Row silently discarded by the backend: it had nothing to do (a Pendiente row sent to
   *  reverse). It adds to no total, so it raises no warning in the UI. */
  | 'ignored';

// Outcome discriminator constants (mirror backend OvertimeApprovalBulkOutcomes).
export const OVERTIME_BULK_OUTCOME = {
  APPROVED: 'approved',
  REVERSED: 'reversed',
  JUSTIFIED: 'justified',
  ALREADY_RESOLVED: 'alreadyResolved',
  NOT_PROCESSED: 'notProcessed',
  IGNORED: 'ignored',
} as const;

// ---------------------------------------------------------------------------
// Mass justification by the reviewer (ASIS-5358)
// ---------------------------------------------------------------------------

/**
 * Entrada de la justificación masiva del revisor desde el listado. Contraparte de
 * `LC_BulkApproveOvertimeIn` para la mitad del flujo que ejecuta el revisor; el mismo endpoint
 * sirve a la acción unitaria del menú de fila, con un solo colaborador.
 *
 * No viajan tramos: el revisor selecciona colaboradores completos y el backend resuelve qué
 * jornadas del corte justificar.
 */
export interface LC_JustifyOvertimeIn {
  monthlyCutId: number;
  employeeIds: string;
  justificationTypeId: number;
  /** Solo se envía cuando el tipo tiene código `OTRO`; el backend rechaza el sobrante. */
  freeComment?: string;
}

/** Resultado por colaborador de la justificación masiva. */
export interface LC_JustifyOvertimeItemOut {
  employeeId: number;
  outcome: OvertimeBulkOutcome;
}

export interface LC_JustifyOvertimeOut {
  totalJustified: number;
  totalAlreadyResolved: number;
  totalNotProcessed: number;
  items: LC_JustifyOvertimeItemOut[];
}

/** Present only when `outcome` is "alreadyResolved": who resolved the record first. */
export interface OvertimeConflict {
  action: 'approved' | 'reversed' | '';
  userName: string;
  currentStatus: OvertimeApprovalStatus | '';
}

export interface LC_BulkApproveOvertimeItemOut {
  employeeId: number;
  outcome: OvertimeBulkOutcome;
  status: OvertimeApprovalStatus | '';
  conflict?: OvertimeConflict | null;
}

export interface LC_BulkApproveOvertimeOut {
  totalApproved: number;
  totalAlreadyResolved: number;
  totalNotProcessed: number;
  items: LC_BulkApproveOvertimeItemOut[];
}

// ---------------------------------------------------------------------------
// Reverse (ASIS-5371)
// ---------------------------------------------------------------------------

export interface LC_ReverseOvertimeIn {
  monthlyCutId: number;
  employeeIds: string;
}

export interface LC_ReverseOvertimeItemOut {
  employeeId: number;
  outcome: OvertimeBulkOutcome;
  status: OvertimeApprovalStatus | '';
  conflict?: OvertimeConflict | null;
}

export interface LC_ReverseOvertimeOut {
  totalReversed: number;
  totalAlreadyResolved: number;
  totalNotProcessed: number;
  items: LC_ReverseOvertimeItemOut[];
}

// ---------------------------------------------------------------------------
// Detail (ASIS-5355)
// ---------------------------------------------------------------------------

export interface LC_GetOvertimeApprovalDetailIn {
  monthlyCutId: number;
  /** Always explicit — this screen never resolves employees via dimensional filters. */
  employeeIds: string;
  counterFilter?: OvertimeCounterKey | null;
  /** Paginación server-side del detalle (ASIS-5358). Aplica a todos los perfiles: el universo
   *  acotado por `employeeIds` puede seguir teniendo cientos de jornadas. */
  page?: number;
  pageSize?: number;
  /** Busca por nombre y RUT (el backend compara el RUT sin puntos ni guion en ambos lados). */
  searchText?: string;
  /** Whitelist backend `OvertimeApprovalDetailOrderByFields`. */
  orderBy?: string;
  orderDirection?: string;
}

export interface LC_OvertimeApprovalDetailItemOut {
  employeeId: number;
  rut: string;
  employeeName: string;
  /** yyyy-MM-dd (mirrors backend DateOnly). */
  workDate: string;
  /** Company-configurable label — never a closed enum. Display only. */
  overtimeType: string;
  /** Technical code ('HorasExtraT1'..'HorasExtraT6') — the stable counterpart of `overtimeType`.
   *  The legacy approval SP matches on this code, not on the company-configurable label, so it is
   *  what must travel back on approve. Also part of the deterministic row id. */
  overtimeTypeCode: string;
  calculatedHours: LC_HourValueOut;
  approvedHours: LC_HourValueOut;
  /** Total the row would end up approved at — an absolute value, not the pending hours. It seeds
   *  the editable "Horas a aprobar" cell and travels back verbatim on approve, where the legacy SP
   *  takes it as the slot's justified hours. */
  hoursToApprove: LC_HourValueOut;
  status: OvertimeApprovalStatus;
  scheduleLabel?: string | null;
  /** IdHorario — technical counterpart of `scheduleLabel`; null when no schedule is assigned that
   *  day. Together with `workDate` it identifies the workday the legacy SP replaces wholesale, so
   *  it is needed both for the row id and for the approve payload. */
  scheduleId?: number | null;
  /** Weekly-compensation rows are approvable, but the legacy SP handles them in a separate branch
   *  and silently drops the row when the flag is not sent explicitly — never leave it undefined. */
  isWeeklyCompensation: boolean;
  entryTime?: string | null;
  exitTime?: string | null;
  lunchStart?: string | null;
  lunchEnd?: string | null;
  approverComment?: string | null;
  /** Only meaningful when overtimeApprovalFlowEnabled is true; otherwise always null. */
  reviewerComment?: string | null;
  /** Only meaningful when overtimeApprovalFlowEnabled is true; otherwise always null. */
  reviewerJustificationType?: string | null;
  /** `true` cuando la fila ya tiene marca `REVISOR` en `caHHEEFlujoAprobacionAuditoria`.
   *  `false` cuando el flujo está apagado (ASIS-5358). */
  isJustifiedByReviewer: boolean;
  /** `"Justificada"` | `"Pendiente"`; `null` cuando el flujo está apagado. A grano de fila NO
   *  existe `"Parcial"`: una jornada/tipo o está justificada o no lo está. */
  reviewerStatus?: OvertimeApprovalStatus | null;
}

export interface LC_GetOvertimeApprovalDetailListOut {
  items: LC_OvertimeApprovalDetailItemOut[];
  counters: LC_OvertimeApprovalCountersOut;
  /** Resolved server-side (caConfiguracionAsistencia.HHEEFlujoAprobacion) — never inferred
   *  client-side. */
  overtimeApprovalFlowEnabled: boolean;
  /** Same field the general list already exposes. Drives the closed-cut banner and disables the
   *  write actions; the backend rejects a closed cut with 409 regardless. */
  isMonthlyCutClosed: boolean;
  /** Paginación server-side del detalle (ASIS-5358) — mismo tipo que usa el listado. */
  pagination?: LC_PaginationMetadataOut | null;
  /** Perfil efectivo del usuario, resuelto server-side desde sus roles. `null` cuando el flujo
   *  está apagado o el usuario no tiene mapeo. Con ambos perfiles prevalece `APROBADOR`. */
  viewerFlowProfile?: OvertimeFlowProfileCode | null;
  /** Descriptor del corte, para el título "HHEE : Detalle (Año - Mes - Descripción)". Viaja en el
   *  response y no por query string: un rótulo del cliente sería falseable. */
  monthlyCutYear: number;
  monthlyCutMonth: number;
  monthlyCutDescription?: string | null;
}

/** Internal UI model for a detail row (colaborador + jornada + tipo). */
export interface OvertimeApprovalDetailRow {
  employeeId: number;
  rut: string;
  employeeName: string;
  workDate: string;
  overtimeType: string;
  /** Technical type code — used for the deterministic row id and for the approve payload. */
  overtimeTypeCode: string;
  calculatedHours: LC_HourValueOut;
  approvedHours: LC_HourValueOut;
  /** Absolute total the row would end up approved at (see the transport type). */
  hoursToApprove: LC_HourValueOut;
  status: OvertimeApprovalStatus;
  scheduleLabel: string | null;
  /** IdHorario — part of the row identity and of the approve payload. */
  scheduleId: number | null;
  isWeeklyCompensation: boolean;
  entryTime: string | null;
  exitTime: string | null;
  lunchStart: string | null;
  lunchEnd: string | null;
  approverComment: string | null;
  reviewerComment: string | null;
  reviewerJustificationType: string | null;
  /** Marca del revisor sobre la fila (ASIS-5358). */
  isJustifiedByReviewer: boolean;
  /** Estado del revisor (`Justificada`/`Pendiente`), o `null` con el flujo apagado. */
  reviewerStatus: OvertimeApprovalStatus | null;
}

/** Internal UI model for the mapped detail response (ASIS-5355, paginado en ASIS-5358). */
export interface OvertimeApprovalDetailPage {
  items: OvertimeApprovalDetailRow[];
  /** Total del universo (después de `searchText`, antes de la paginación). */
  totalRecords: number;
  counters: OvertimeApprovalCounters;
  overtimeApprovalFlowEnabled: boolean;
  isMonthlyCutClosed: boolean;
  viewerFlowProfile: OvertimeFlowProfileCode | null;
  monthlyCutYear: number;
  monthlyCutMonth: number;
  monthlyCutDescription: string | null;
}

/** Internal UI model for the top counters. `justified` lo alimentan el listado y el detalle. */
export interface OvertimeApprovalCounters {
  calculated: LC_HourValueOut;
  pending: LC_HourValueOut;
  approved: LC_HourValueOut;
  notApproved: LC_HourValueOut;
  justified: LC_HourValueOut;
}

/** Internal UI model for a page of the grid. */
export interface OvertimeApprovalPage {
  items: OvertimeApprovalRow[];
  totalRecords: number;
  counters: OvertimeApprovalCounters;
  isMonthlyCutClosed: boolean;
  /** Perfil efectivo del usuario, para decidir qué pinta el listado (ASIS-5358). */
  viewerFlowProfile: OvertimeFlowProfileCode | null;
}

// ---------------------------------------------------------------------------
// Approve from detail (ASIS-5356)
// ---------------------------------------------------------------------------

/**
 * Maximum length of the approval comment. Mirrors backend
 * `OvertimeApprovalConstants.COMMENT_MAX_LENGTH`. Not a UI preference: it is the real width of
 * `caHorasExtraJustificadas.Comentario` (VARCHAR(50)), so never hardcode 50 elsewhere.
 */
export const OVERTIME_COMMENT_MAX_LENGTH = 50;

/** One selected detail row (collaborator + workday + overtime type) with its hours to approve. */
export interface LC_ApproveOvertimeDetailItemIn {
  employeeId: number;
  /** yyyy-MM-dd (mirrors backend DateOnly). */
  workDate: string;
  /** Technical code ('HorasExtraT1'..'HorasExtraT6'), never the company-configurable label: the
   *  legacy SP joins the type table on this code and the label would not match. */
  overtimeTypeCode: string;
  /** IdHorario, or null when the workday has no schedule. The backend converts null to the 0 the
   *  legacy SP expects — always send the key, never omit it. */
  scheduleId: number | null;
  /** Always explicit: a missing flag makes the legacy SP drop the row without reporting an
   *  error. */
  isWeeklyCompensation: boolean;
  /** Absolute total of decimal hours the row ends up approved at — it REPLACES whatever was
   *  approved before, it is not an increment over `knownApprovedHours`. An explicit 0 is valid: it
   *  un-approves the slot. */
  hoursToApprove: number;
  /** Approved hours as the client saw them when the detail loaded (optimistic concurrency): if the
   *  server-side value has moved, the row is reported back as "alreadyResolved" instead of
   *  overwriting whoever resolved it first. */
  knownApprovedHours: number;
}

export interface LC_ApproveOvertimeDetailIn {
  monthlyCutId: number;
  /** Required, max OVERTIME_COMMENT_MAX_LENGTH characters. */
  comment: string;
  items: LC_ApproveOvertimeDetailItemIn[];
}

/** Result for a single row of the batch. Identity travels in full so the grid can reconcile rows
 *  without relying on array order. */
export interface LC_ApproveOvertimeDetailItemOut {
  employeeId: number;
  workDate: string;
  overtimeTypeCode: string;
  outcome: OvertimeBulkOutcome;
  status: OvertimeApprovalStatus | '';
}

export interface LC_ApproveOvertimeDetailOut {
  totalApproved: number;
  totalAlreadyResolved: number;
  totalNotProcessed: number;
  items: LC_ApproveOvertimeDetailItemOut[];
}

// ---------------------------------------------------------------------------
// Reverse from detail (ASIS-5357)
// ---------------------------------------------------------------------------

/** One selected detail row (collaborator + workday + overtime type) to reverse. */
export interface LC_ReverseOvertimeDetailItemIn {
  employeeId: number;
  /** yyyy-MM-dd (mirrors backend DateOnly). */
  workDate: string;
  /** Technical code ('HorasExtraT1'..'HorasExtraT6'), never the company-configurable label: it is
   *  the key the backend reconciles the row with against its own fresh read of the detail. */
  overtimeTypeCode: string;
  /** IdHorario, or null when the workday has no schedule. Always send the key, never omit it. */
  scheduleId: number | null;
  /** Always explicit. Weekly-compensation rows are accepted, but the legacy SP does not delete
   *  them — it only clears their metadata — so their hours stay approved and they come back
   *  reported as not processed. */
  isWeeklyCompensation: boolean;
  /** Approved hours as the client saw them when the detail loaded (optimistic concurrency): if the
   *  server-side value has moved, the row is reported back as "alreadyResolved" instead of
   *  overwriting whoever resolved it first. */
  knownApprovedHours: number;
}

/** No comment travels here: reversing does not ask for one. The identifier of the justification to
 *  delete is resolved server-side — accepting it from the client would allow deleting arbitrary
 *  records. */
export interface LC_ReverseOvertimeDetailIn {
  monthlyCutId: number;
  items: LC_ReverseOvertimeDetailItemIn[];
}

/** Result for a single row of the batch. Identity travels in full so the grid can reconcile rows
 *  without relying on array order. */
export interface LC_ReverseOvertimeDetailItemOut {
  employeeId: number;
  workDate: string;
  overtimeTypeCode: string;
  outcome: OvertimeBulkOutcome;
  status: OvertimeApprovalStatus | '';
}

export interface LC_ReverseOvertimeDetailOut {
  totalReversed: number;
  totalAlreadyResolved: number;
  /** Rows that entered the batch and did not end up reversed (group lock, row already gone from
   *  the detail, or weekly compensation). Rows discarded silently ('ignored') are NOT counted
   *  here — that is what keeps them from raising a warning. */
  totalNotProcessed: number;
  items: LC_ReverseOvertimeDetailItemOut[];
}

// ---------------------------------------------------------------------------
// Reviewer justification (ASIS-5358)
// ---------------------------------------------------------------------------

/**
 * Máximo del comentario libre que exige el tipo `OTRO`. Espeja
 * `OvertimeApprovalConstants.JUSTIFICATION_FREE_COMMENT_MAX_LENGTH`: es el ancho real de
 * `caHHEEFlujoAprobacionAuditoria.ComentarioTexto`, no una preferencia de UI.
 */
export const OVERTIME_JUSTIFICATION_FREE_COMMENT_MAX_LENGTH = 500;

/**
 * Tope de filas por operación de justificación. Espeja
 * `OvertimeApprovalConstants.MAX_JUSTIFY_DETAIL_ROWS`.
 */
export const MAX_JUSTIFY_DETAIL_ROWS = 2000;

/** Un tipo del catálogo `caHHEEFlujoAprobacionComentario`. */
export interface LC_OvertimeJustificationTypeItemOut {
  id: number;
  code: string;
  text: string;
  /** `true` cuando el código es `OTRO`: el revisor debe además escribir un comentario libre. Se
   *  resuelve server-side desde el código, JAMÁS desde el texto de la etiqueta, que es
   *  editable. */
  requiresFreeComment: boolean;
}

export interface LC_GetOvertimeJustificationTypesListOut {
  items: LC_OvertimeJustificationTypeItemOut[];
}

/** Internal UI model for a justification type. */
export interface OvertimeJustificationType {
  id: number;
  code: string;
  text: string;
  requiresFreeComment: boolean;
}

/** One selected detail row (collaborator + workday + overtime type) to justify. */
export interface LC_JustifyOvertimeDetailItemIn {
  employeeId: number;
  /** yyyy-MM-dd (el backend lo recibe como DateTime). */
  workDate: string;
  /** IdHorario, o null cuando la jornada no tiene horario asignado. */
  scheduleId: number | null;
  /** Technical code ('HorasExtraT1'..'HorasExtraT6'), never the company-configurable label. */
  overtimeTypeCode: string;
}

/**
 * No viajan horas: las resuelve el backend releyendo el detalle. Tampoco `isWeeklyCompensation`
 * — a diferencia de aprobar, el SP de justificación no ramifica por ese flag.
 */
export interface LC_JustifyOvertimeDetailIn {
  monthlyCutId: number;
  justificationTypeId: number;
  /** Obligatorio y ≤ 500 solo cuando el tipo tiene `requiresFreeComment`; prohibido si no. */
  freeComment?: string | null;
  items: LC_JustifyOvertimeDetailItemIn[];
}

export interface LC_JustifyOvertimeDetailItemOut {
  employeeId: number;
  employeeName: string;
  workDate: string;
  overtimeTypeCode: string;
  outcome: OvertimeBulkOutcome;
  /** Estado del revisor tras la operación: `"Justificada"` | `"Pendiente"`. */
  reviewerStatus: OvertimeApprovalStatus | '';
}

export interface LC_JustifyOvertimeDetailOut {
  totalJustified: number;
  totalNotProcessed: number;
  items: LC_JustifyOvertimeDetailItemOut[];
}
